/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import locale from 'date-fns/locale/de';
import {
  RequestValueKeys,
  AppointmentDateTypes,
  Sources,
  RequestCallTypes,
} from '@medflex/shared-types';
import LinkifyText from '../LinkifyText';
import SchedulePicker from '../SchedulePicker/SchedulePicker';
import CallSteps from './CallSteps';
import AudioValue from './AudioValue';
import ExternalDocumentLink from './ExternalDocumentLink';
import AudioRecordingUrl from './AudioRecordingUrl';
import FileValue from './FileValue';
import RepresentedPerson from '../RepresentedPerson';
import InquiryCallDetails from './InquiryCallDetails/InquiryCallDetails';

const Title = ({ children }) => (
  <div className="text-sm font-semibold leading-5 text-slate-600">{children}</div>
);

Title.propTypes = {
  children: PropTypes.node.isRequired,
};

const Value = ({ title = '', value = '', className = '' }) => (
  <div className={className}>
    {title && <Title>{title}</Title>}
    <div className="whitespace-pre-line break-words">
      <LinkifyText>{value}</LinkifyText>
    </div>
  </div>
);

Value.propTypes = {
  title: PropTypes.node,
  value: PropTypes.node.isRequired,
  className: PropTypes.string,
};

const InquiryValue = ({
  requestId,
  valueKey,
  value,
  source,
  onOpenFile,
  onFetchAudioSource,
  onDownloadFile,
  onWriteExamination = null,
  isPatient = true,
  showFileActions = false,
}) => {
  const { t } = useTranslation('components');
  if (!value) {
    return null;
  }

  switch (valueKey) {
    case RequestValueKeys.REPRESENTED_PERSON:
      return <RepresentedPerson userDetails={value} />;
    case RequestValueKeys.PATIENT_INSURANCE_TYPE_OR_MEDFLEX:
      if (value === 'medflex') {
        return (
          <Value
            title={t(`PamInquiry.patientStatus.title`)}
            value={t(`PamInquiry.patientStatus.existing`)}
          />
        );
      }

      return (
        <>
          <Value
            title={t('PamInquiry.patientStatus.title')}
            value={t('PamInquiry.patientStatus.new')}
          />
          <Value
            title={t(`PamInquiry.${valueKey}.title`)}
            value={t(`PamInquiry.${valueKey}.${value}`)}
          />
        </>
      );
    case RequestValueKeys.APPOINTMENT_DATE_TYPE:
      // If the appointment date type is SUGGESTED_DATE, value will come from APPOINTMENT_AVAILABILITY
      if (value === AppointmentDateTypes.SUGGESTED_DATE) {
        return null;
      }

      return (
        <Value
          title={t(`PamInquiry.${valueKey}.title`)}
          value={t(`PamInquiry.${valueKey}.${value}`)}
        />
      );
    case RequestValueKeys.APPOINTMENT_AVAILABILITY:
      return (
        <>
          <Value
            title={t(`PamInquiry.${valueKey}.title`)}
            value={t(`PamInquiry.${valueKey}.suggested`)}
          />
          <SchedulePicker initialSchedule={value} readOnly />
        </>
      );
    case RequestValueKeys.EXISTING_APPOINTMENT_DATE_TIME:
      return (
        <Value
          title={t(`PamInquiry.${valueKey}.title`)}
          value={format(new Date(value), 'DD. MMM. YYYY, HH:mm', { locale })}
        />
      );
    case RequestValueKeys.FILES:
      if (value.length === 0) {
        return null;
      }

      return (
        <div>
          <Title>{t(`PamInquiry.${valueKey}.title`)}</Title>
          <div className="flex flex-row flex-wrap gap-4">
            {value.map(file =>
              ['audio/mpeg', 'audio/vnd.wave'].includes(file.mediaType) ? (
                <AudioValue
                  key={file.id}
                  file={file}
                  onFetchAudioSource={onFetchAudioSource}
                  onWriteExamination={onWriteExamination}
                />
              ) : (
                <FileValue
                  key={file.id}
                  file={file}
                  onOpenFile={onOpenFile}
                  onDownloadFile={onDownloadFile}
                  onWriteExamination={onWriteExamination}
                  showActionButtons={showFileActions}
                />
              ),
            )}
          </div>
        </div>
      );
    case RequestValueKeys.PHONE_AUDIO_RECORDING_URL:
      if (isPatient) return null;

      return (
        <div>
          <Title>{t(`PamInquiry.${valueKey}.title`)}</Title>
          <AudioRecordingUrl
            url={value}
            recordingTitle={t(`PamInquiry.${valueKey}.recording`)}
            recordingLink={t(`PamInquiry.${valueKey}.link`)}
          />
        </div>
      );
    case RequestValueKeys.FOLLOW_UP_PRESCRIPTION: {
      const title = (
        <>
          {t(`PamInquiry.${valueKey}.title`)}{' '}
          <span className="font-normal">{t(`PamInquiry.${valueKey}.subtitle`)}</span>
        </>
      );

      if ([Sources.MESSENGER, Sources.CONTACT_PAGE].includes(source)) {
        return (
          <div>
            <Title>{title}</Title>
            <ul className="list-disc list-outside ml-4">
              {value.split('\n').map(entry => (
                <li key={entry} className="ml-2">
                  {entry}
                </li>
              ))}
            </ul>
          </div>
        );
      }

      return <Value title={title} value={value} />;
    }
    case RequestValueKeys.DOCUMENTS:
      if (value.length === 0) {
        return null;
      }

      return (
        <div>
          <Title>{t(`PamInquiry.${valueKey}.title`)}</Title>
          <div className="space-y-4 divide-y">
            {value.map(({ title, createdAt, url }) => (
              <ExternalDocumentLink
                key={`externalDocument_${title}`}
                title={title}
                createdAt={createdAt}
                url={url}
                isPatient={isPatient}
              />
            ))}
          </div>
        </div>
      );
    case RequestValueKeys.CALL: {
      if (value.type === RequestCallTypes.CONVERSATIONAL) {
        return (
          <InquiryCallDetails
            key={requestId}
            requestId={requestId}
            summary={value.summary}
            todos={value.todos}
            messages={value.messages}
            files={value.files}
            onFetchAudioSource={onFetchAudioSource}
          />
        );
      }

      const original = value.steps.map(({ original }) => original);
      const translated = value.steps
        .filter(({ translated }) => translated)
        .map(({ translated }) => translated);

      return (
        <div>
          <Title>{t(`PamInquiry.${valueKey}.title`)}</Title>
          <div className="flex flex-row flex-wrap gap-4">
            {translated.length > 0 && (
              <>
                <CallSteps
                  steps={translated}
                  onFetchAudioSource={onFetchAudioSource}
                  onWriteExamination={onWriteExamination}
                />
                <div className="px-2 w-full">
                  <p className="pb-2 pt-4 w-full border-b border-b-slate-300 text-slate-700 font-semibold">
                    {t(`PamInquiry.${valueKey}.titleOriginal`)}
                  </p>
                </div>
              </>
            )}
            <CallSteps
              steps={original}
              onFetchAudioSource={onFetchAudioSource}
              onWriteExamination={onWriteExamination}
            />
          </div>
        </div>
      );
    }
    case RequestValueKeys.APPOINTMENT_REASON:
    case RequestValueKeys.APPOINTMENT_ACTION:
    case RequestValueKeys.APPOINTMENT_TYPE:
    case RequestValueKeys.DIAGNOSIS_REQUEST_TYPE:
    case RequestValueKeys.DOCUMENTS_DELIVERY:
      return (
        <Value
          title={t(`PamInquiry.${valueKey}.title`)}
          value={t(`PamInquiry.${valueKey}.${value}`)}
        />
      );
    default:
      return <Value title={t(`PamInquiry.${valueKey}.title`)} value={value} />;
  }
};

InquiryValue.propTypes = {
  requestId: PropTypes.string.isRequired,
  valueKey: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  source: PropTypes.oneOf(Object.values(Sources)).isRequired,
  onOpenFile: PropTypes.func,
  onDownloadFile: PropTypes.func,
  onWriteExamination: PropTypes.func,
  onFetchAudioSource: PropTypes.func,
  isPatient: PropTypes.bool,
  showFileActions: PropTypes.bool,
};

export default InquiryValue;
